import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { Contract } from '../../interfaces/contract';
import { DatePipe, NgClass, NgIf } from '@angular/common';
import { BaseComponent } from '../../components/base.component';
import { InstallmentsSliderComponent } from '../../components/installments-slider/installments-slider.component';
import { ContractOptionComponent } from '../../components/contract-option/contract-option.component';
import { HeaderButton } from '../../interfaces/header-button';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from '../../services/data.service';
import { ContractService } from '../../services/contract.service';
import { LoadingService } from '../../services/loading.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { ChangeDueDateModalComponent } from '../../components/change-due-date-modal/change-due-date-modal.component';
import { MiscService } from '../../services/misc.service';
import { DocumentsService } from '../../services/documents.service';
import { finalize, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DownloadFileService } from '../../services/download-file.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AlertService } from '../../components/alert/alert.service';
import { ConfirmModalComponent } from '../../components/confirm-modal/confirm-modal.component';

@Component({
    selector: 'mobilize-contract-details',
    animations: [
        trigger('toggleHeight', [
            state('void', style({ height: '0px', overflow: 'hidden', opacity: 0 })),
            state('*', style({ height: '*', overflow: 'hidden', opacity: 1 })),
            transition('void => *', [
                animate('200ms ease-out')
            ]),
            transition('* => void', [
                animate('300ms ease-in')
            ])
        ])
    ],
    imports: [
        PageHeaderComponent,
        NgClass,
        InstallmentsSliderComponent,
        ContractOptionComponent,
        DatePipe,
        MatProgressSpinner
    ],
    templateUrl: './contract-details.component.html',
    styleUrl: './contract-details.component.scss'
})
export class ContractDetailsComponent extends BaseComponent implements OnInit {

    private _route = inject(ActivatedRoute);
    private _router = inject(Router);
    private _data = inject(DataService);
    private _alert = inject(AlertService);
    private _loading = inject(LoadingService);
    private _downloadFile = inject(DownloadFileService);
    private _snackbar = inject(MatSnackBar);
    private _misc = inject(MiscService);
    private _dialog = inject(MatDialog);
    private _documents = inject(DocumentsService);

    @ViewChild('documentsSection') documentsSection!: ElementRef;
    @ViewChild('paymentScheduleSection') paymentScheduleSection!: ElementRef;
    @ViewChild('optionsSection') optionsSection!: ElementRef;

    constructor(private _contracts: ContractService) {
        super();

        this._route.params.subscribe((rsp: any) => {
            this.contractId = rsp.id;
        });
    }

    contractId!: number;
    showMoreSections: any = {
        general: false,
        financial: false,
        good: false
    }
    processing = false;
    payedAmount = 0;
    headerButton: HeaderButton = {
        label: 'Inchide',
        action: 'route',
        actionData: {
            route: '/contracts',
            method: ''
        },
        className: 'btn-clear'
    }
    services: any[] = [];

    contract!: Contract;
    dataLoaded = false;
    maxInstallments = 0;

    ngOnInit() {
        this._loading.setLoadingState(true);
        this._contracts.getContract(this.contractId).subscribe(rsp => {
            this.contract = rsp;
            this.contract.documents = [
                {
                    type: 'scadentar',
                    name: 'Scadentar'
                },
                {
                    type: 'contract',
                    name: 'Contract'
                }
            ]
            this.dataLoaded = true;
            this.payedAmount = 0;
            let count = 0;
            this.contract.contract_schedules.forEach((schedule: any) => {
                if (count < this.contract.previous_instalments && schedule.rate_name !== 'Valoare reziduala') {
                    this.payedAmount += parseFloat(schedule.principal_amount);
                    if (schedule.schedule_type_id === 4) {
                        count++;
                    }
                }
            });

            this.maxInstallments = this.contract.contract_duration;
            this._loading.setLoadingState(false);
            const services = this.contract.contract_services_list.filter(item => item.service_code !== 'LS_COM_GL');
            services.forEach((service: any) => {
                if (this.isServiceActive(service.service_start_date, service.service_end_date)) {
                    service.active = true;
                }
            });
            this.services.push(...services);
            if (this.contract.casco_insurance) {
                this.services.push(this.contract.casco_insurance)
            }
            if (this.contract.rca_insurance) {
                this.services.push(this.contract.rca_insurance)
            }
            if (this.contract.adi_insurance) {
                this.services.push(this.contract.adi_insurance)
            }
        });
    }

    getDocument(type: string) {
        if (this.processing) return;

        this.processing = true;

        this._documents.getContractDocuments({contractNumber: this.contract.contract_number,  type}).pipe(
            finalize(() => {
                this.processing = false;
            }),
            catchError(((err: any) => {
                this._dialog.open(ConfirmModalComponent, {
                    data: {
                        title: 'Documentul nu a putut fi descarcat.',
                        content: `Te rog sa transmiti o solicitare pentru acest document catre rcileasing.romania@mobilize-fs.com`,
                        noCancel: true,
                        confirmText: 'Ok'
                    }
                });
                return of(false);
            }))
        )
            .subscribe((response: HttpResponse<Blob>) => {
                this._downloadFile.downloadFileFromResponse(response);
            });
    }

    showMore(section: string) {
        this.showMoreSections[section] = !this.showMoreSections[section];
    }

    earlyRepayment() {
        this._router.navigateByUrl('/early-repayment', {state: {data: {contract: this.contract}}})
    }

    private isServiceActive(serviceStartDate: string, serviceEndDate: string): boolean {
        const currentDate = new Date();
        const startDate = new Date(serviceStartDate);
        const endDate = new Date(serviceEndDate);

        return currentDate >= startDate && currentDate <= endDate;
    }

    changeDueDate() {
        this._dialog.open(ChangeDueDateModalComponent, {
            width: '500px',
            data: {
                contract: this.contract,
            }
        }).afterClosed().subscribe((rsp: any) => {
            if (rsp.change) {
                this._misc.sendChangeDueDate({
                    contract_id: this.contract.contract_id,
                    due_date: rsp.dueDate,
                    contract_site_id: this.contract.contract_site_id,
                })
                    .pipe(
                        catchError((error) => {
                            console.log(error)
                            if (error.error.statusCode === 409) {
                                this._snackbar.open('Ati efectuat deja o schimbare in ultimele 6 luni', 'Ok', {
                                    panelClass: 'error-snack'
                                });
                            }
                            if (error.error.statusCode === 400) {
                                this._snackbar.open('Schimbarea scadentei se poate face doar dupa emiterea primei rate.', 'Ok', {
                                    panelClass: 'error-snack'
                                });
                            }
                            return throwError(error);
                        })

                    )
                    .subscribe((res: any) => {
                        this._snackbar.open('Modificarea a fost transmisa spre operare. Vei primi pe mail noul scadentar modificat.', 'Ok')
                    })
            }
        })
    }

    focusSection(section: string) {
        switch (section) {
            case 'documents':
                this.documentsSection.nativeElement.scrollIntoView({behavior: 'smooth'});
                break;

            case 'payment-schedule':
                this.paymentScheduleSection.nativeElement.scrollIntoView({behavior: 'smooth'});
                break;

            case 'options':
                this.optionsSection.nativeElement.scrollIntoView({behavior: 'smooth'});
                break;

            case 'history':
                this._data.setData({contractName: this.contract.contract_id});
                this._router.navigateByUrl(`contract-details/${this.contractId}/history`);
                break;

        }
    }
}
